import { type AdminEpisodeDetail, ENDING_RARITY_META, type FlagCondition, type StoryGraphIssue } from "@storyline/shared";
import Pill from "@/src/common/components/pill";
import SectionCard from "@/src/common/components/section-card";
import { resolveSubjectLabel } from "@/src/stories/utils/graph-view";

const conditionLabel = (condition: FlagCondition): string => `${condition.flag} ${condition.cmp} ${String(condition.value)}`;

type GraphSidePanelProps = {
	episode: AdminEpisodeDetail;
	issues: StoryGraphIssue[];
};

const GraphSidePanel: React.FC<GraphSidePanelProps> = ({ episode, issues }) => (
	<div className="flex flex-col gap-4">
		{issues.length > 0 ? (
			<SectionCard title={`Problèmes (${issues.length})`} description="Corrigez le graphe avant de valider l'histoire.">
				<ul className="flex flex-col gap-2">
					{issues.map((issue) => (
						<li key={`${issue.code}-${issue.subjectId ?? "graph"}`} className="flex flex-col gap-0.5 border-danger/40 border-l-2 pl-3">
							<span className="text-danger text-xs uppercase tracking-wide">{resolveSubjectLabel(episode, issue.subjectId)}</span>
							<span className="text-foreground text-sm">{issue.message}</span>
						</li>
					))}
				</ul>
			</SectionCard>
		) : null}

		<SectionCard title={`Flags (${episode.flags.length})`}>
			{episode.flags.length === 0 ? (
				<p className="text-muted text-sm">Aucun flag.</p>
			) : (
				<div className="flex flex-wrap gap-2">
					{episode.flags.map((flag) => (
						<Pill key={flag.name}>
							{flag.name} · {flag.type} · {String(flag.initialValue)}
						</Pill>
					))}
				</div>
			)}
		</SectionCard>

		<SectionCard title={`Fins (${episode.endings.length})`}>
			<ul className="flex flex-col gap-1">
				{episode.endings.map((ending) => (
					<li key={ending.id} className="flex items-center justify-between text-sm">
						<span className="text-foreground">
							{ending.endingNumber}. {ending.name}
						</span>
						<span className="text-muted text-xs">{ENDING_RARITY_META[ending.rarity].label}</span>
					</li>
				))}
			</ul>
		</SectionCard>

		<SectionCard title={`Règles de fin (${episode.endingRules.length})`}>
			<ul className="flex flex-col gap-2">
				{episode.endingRules
					.slice()
					.sort((a, b) => b.priority - a.priority)
					.map((rule) => {
						const ending = episode.endings.find((candidate) => candidate.id === rule.endingId);
						return (
							<li key={rule.id} className="flex flex-col gap-1 rounded-lg border border-border-soft bg-glass p-2 text-sm">
								<div className="flex items-center justify-between">
									<span className="text-foreground">→ {ending?.name ?? rule.endingId}</span>
									<span className="text-muted text-xs">priorité {rule.priority}</span>
								</div>
								{rule.conditions.length === 0 ? (
									<Pill tone="warning">par défaut</Pill>
								) : (
									<span className="text-muted text-xs">{rule.conditions.map(conditionLabel).join(" ET ")}</span>
								)}
							</li>
						);
					})}
			</ul>
		</SectionCard>
	</div>
);

export default GraphSidePanel;
